import React, { useState } from 'react';
import { Button, Container, Typography, Box, TextField, Paper, Grid, IconButton, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { connect } from 'react-redux';

const Input = styled('input')({
	display: 'none',
});

const Alert = React.forwardRef(function Alert(props, ref) {
	return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function Base64Encoder() {
	const [file, setFile] = useState(null);
	const [fileInfo, setFileInfo] = useState(null);
	const [base64String, setBase64String] = useState('');
	const [previewUrl, setPreviewUrl] = useState('');
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [dragActive, setDragActive] = useState(false);

	const handleFileChange = (event) => {
		const selectedFile = event.target.files[0];
		if (selectedFile) {
			setFile(selectedFile);
			setFileInfo({
				name: selectedFile.name,
				size: (selectedFile.size / 1024).toFixed(2) + ' KB',
			});
			setPreviewUrl(URL.createObjectURL(selectedFile));
		}
	};

	const encodeToBase64 = () => {
		if (file) {
			const reader = new FileReader();
			reader.onload = (e) => {
				const base64 = e.target.result;
				setBase64String(base64);
			};
			reader.readAsDataURL(file);
		}
	};

	const copyToClipboard = () => {
		navigator.clipboard.writeText(base64String);
		setOpenSnackbar(true);
	};

	const handleCloseSnackbar = (event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		setOpenSnackbar(false);
	};

	const handleDragOver = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setDragActive(true);
	};

	const handleDragLeave = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setDragActive(false);
	};

	const handleDrop = (event) => {
		event.preventDefault();
		event.stopPropagation();
		setDragActive(false);
		const selectedFile = event.dataTransfer.files[0];
		if (selectedFile) {
			setFile(selectedFile);
			setFileInfo({
				name: selectedFile.name,
				size: (selectedFile.size / 1024).toFixed(2) + ' KB',
			});
			setPreviewUrl(URL.createObjectURL(selectedFile));
		}
	};

	const handleClick = (event) => {
		event.preventDefault();
		document.getElementById('contained-button-file').click();
	};

	const renderPreview = () => {
		if (!previewUrl) return null;
		let previewElement;

		if (file.type.startsWith('image/')) {
			previewElement = <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }} />;
		} else if (file.type.startsWith('video/')) {
			previewElement = (
				<video controls style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}>
					<source src={previewUrl} type={file.type} />
					Your browser does not support the video tag.
				</video>
			);
		} else if (file.type.startsWith('audio/')) {
			previewElement = (
				<audio controls style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '4px' }}>
					<source src={previewUrl} type={file.type} />
					Your browser does not support the audio element.
				</audio>
			);
		} else {
			previewElement = <Typography>Preview not available for this file type.</Typography>;
		}

		return (
			<Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
				{previewElement}
			</Box>
		);
	};

	return (
		<Container
			maxWidth="xl"
			sx={{
				mt: 2,
				px: { xs: 1, sm: 2, md: 3 },
				width: '100%',
				maxWidth: '1600px',
			}}
		>
			<Typography variant="h4" gutterBottom align="left" sx={{ fontWeight: 'bold', mb: 1 }}>
				Base64 Encoder
			</Typography>
			<Typography sx={{ mb: 2 }}>
				The Base64 Encoder is a user-friendly tool designed to transform any file into Base64 format quickly and effortlessly. <br></br>To use it,
				simply choose a file from your device and select the 'Encode to Base64' option. The tool will promptly process your file and present the encoded
				result, which you can copy with a single click. This versatile encoder automatically recognizes the file type and adjusts the output format
				accordingly, ensuring optimal results for various file types.
			</Typography>
			<Grid container spacing={2}>
				<Grid item xs={12} md={6}>
					<Paper elevation={3} sx={{ p: 2, height: '100%', borderRadius: 2, backgroundColor: '#f5f5f5' }}>
						<Typography variant="h6" gutterBottom sx={{ color: '#3f51b5' }}>
							File
						</Typography>
						<Box
							sx={{
								border: '2px dashed #3f51b5',
								borderRadius: 2,
								p: 3,
								textAlign: 'center',
								cursor: 'pointer',
								mb: 2,
								'&:hover': { backgroundColor: '#e8eaf6' },
								backgroundColor: dragActive ? '#e8eaf6' : 'transparent',
							}}
							onDragOver={handleDragOver}
							onDragLeave={handleDragLeave}
							onDrop={handleDrop}
							onClick={handleClick}
						>
							<label htmlFor="contained-button-file" style={{ width: '100%', height: '100%', cursor: 'pointer' }}>
								<Input
									accept="image/*,video/*,audio/*"
									id="contained-button-file"
									type="file"
									onChange={handleFileChange}
									onClick={(event) => event.stopPropagation()}
								/>
								<Box sx={{ pointerEvents: 'none' }}>
									<FileUploadIcon sx={{ fontSize: 48, color: '#3f51b5', mb: 1 }} />
									<Typography>{fileInfo ? `${fileInfo.name} (${fileInfo.size})` : 'Drag and drop a file here or click to select'}</Typography>
								</Box>
							</label>
						</Box>
						{renderPreview()}
						<Button
							variant="contained"
							fullWidth
							onClick={encodeToBase64}
							disabled={!file}
							sx={{
								mt: 2,
								backgroundColor: '#3f51b5',
								'&:hover': { backgroundColor: '#303f9f' },
								'&:disabled': { backgroundColor: '#9fa8da' },
							}}
						>
							Encode to Base64
						</Button>
					</Paper>
				</Grid>
				<Grid item xs={12} md={6}>
					<Paper elevation={3} sx={{ p: 2, height: '100%', borderRadius: 2, backgroundColor: '#f5f5f5' }}>
						<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
							<Typography variant="h6" sx={{ color: '#3f51b5' }}>
								Base64 Result
							</Typography>
							<IconButton onClick={copyToClipboard} size="small">
								<ContentCopyIcon />
							</IconButton>
						</Box>
						<TextField
							multiline
							rows={13}
							fullWidth
							variant="outlined"
							value={base64String}
							InputProps={{
								readOnly: true,
								sx: {
									backgroundColor: '#ffffff',
									'& .MuiOutlinedInput-notchedOutline': {
										borderColor: '#3f51b5',
									},
								},
							}}
						/>
					</Paper>
				</Grid>
			</Grid>
			<Snackbar open={openSnackbar} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
				<Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
					Copied to clipboard!
				</Alert>
			</Snackbar>
		</Container>
	);
}

const stateToProps = (state) => ({
	access: state.access,
	location: state.router.location,
	product: state.product.maytapi,
	user: state.access.user.loggedUserObj,
});

export default connect(stateToProps)(Base64Encoder);
